import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * A drill-down breadcrumb path that survives leaving the page and coming back.
 *
 * The report pages (Balance Sheet, Profit & Loss, Group Summary) drill in place
 * by pushing breadcrumbs into component state, but opening a ledger is a real
 * route change to /reports/ledger. That unmounts the report, so plain useState
 * loses the path — pressing Back remounted the page at its ROOT instead of the
 * sub-group the user came from, no matter how deep they had drilled.
 *
 * Mirroring the path into the query string fixes it: Back returns to the exact
 * URL the user left, and the path is read straight back out of it on mount. It
 * also makes a drilled view survive a refresh and be shareable as a link.
 *
 * In-page drills write with `replace`, so walking down three levels does NOT
 * leave three history entries behind — Back still means "leave this report",
 * exactly as it did before. Only the URL's depth marker changes.
 *
 * Usage is a drop-in for useState:
 *     const [crumbs, setCrumbs] = useDrillPath<Crumb>();
 */
export default function useDrillPath<T>(
  param = 'drill',
): [T[], React.Dispatch<React.SetStateAction<T[]>>] {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read once, on mount — this is what restores the depth on the way back.
  // A malformed or hand-edited param must not blank the page, so parsing
  // failures fall back to the root view.
  const [crumbs, setCrumbs] = useState<T[]>(() => {
    const raw = searchParams.get(param);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as T[]) : [];
    } catch {
      return [];
    }
  });

  // Seeded with what was already in the URL so the first render is a no-op
  // rather than an immediate redundant navigation.
  const lastWritten = useRef<string | null>(searchParams.get(param));

  useEffect(() => {
    const encoded = crumbs.length ? JSON.stringify(crumbs) : null;
    if (lastWritten.current === encoded) return;
    lastWritten.current = encoded;
    // Functional form: other params on this URL (date_from / date_to) are
    // preserved rather than clobbered by a stale snapshot.
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        if (encoded) next.set(param, encoded);
        else next.delete(param);
        return next;
      },
      { replace: true },
    );
  }, [crumbs, param, setSearchParams]);

  return [crumbs, setCrumbs];
}
